const resources = {
  en: {
    debtManager: {
      title: 'Debt Manager',
      pageDescription: 'Track who you owe, who owes you, and keep the debt ledger readable without mixing active and archived items.',
      actions: {
        add: 'Add debt',
        edit: 'Edit',
        history: 'History',
        archive: 'Archive',
        restore: 'Restore',
        delete: 'Delete',
        resolve: 'Resolve without payment',
        paymentTasks: 'Create payment tasks',
        cancel: 'Cancel',
        confirmDeleteTitle: 'Delete this debt?',
        confirmArchiveTitle: 'Move this debt to archive?',
        confirmRestoreTitle: 'Restore this debt to current?',
      },
      accessibility: {
        debtActions: 'Debt actions',
      },
      summary: {
        tracked: {
          label: 'Tracked debts',
          supporting: 'Current and archived items',
        },
        active: {
          label: 'Active debts',
          supporting: 'Still have an open balance',
        },
        settled: {
          label: 'Settled items',
          supporting: 'Can be moved to archive',
        },
        outstanding: {
          label: 'Open balance',
          estimatedLabel: 'Estimated open balance',
          supporting: '{{count}} archived',
          mixedValue: 'Mixed currencies',
        },
      },
      valuation: {
        estimatedTitle: 'Open debt balance uses explicit valuation',
        estimatedDescription: 'This summary values debt balances in {{currency}} from native debt records and cached FX quotes.',
        staleTitle: 'Open debt balance uses stale FX quotes',
        staleDescription: 'This summary values debt balances in {{currency}}, but some FX quotes are stale. Totals may lag until quotes refresh.',
        degradedTitle: 'Open debt balance is partially degraded',
        degradedDescription: 'This summary values debt balances in {{currency}}, but some FX quotes are missing or failed. Totals may be incomplete until refresh succeeds.',
        partialTitle: 'Open debt balance is only partially valued',
        partialDescription: 'This summary values debt balances in {{currency}}, but some debt records cannot be valued with the current quote set.'
      },
      sections: {
        registry: {
          title: 'Debt registry',
          description: 'Keep active debts in view, archive settled ones, and record borrow/repay activity from one place.',
        },
      },
      notices: {
        archiveTitle: 'Archive settled debts',
        archiveDescription: '{{count}} settled debt can be archived to keep the active list focused.',
      },
      list: {
        empty: 'No debts yet',
        emptyArchived: 'No archived debts yet',
        from: 'From {{name}}',
        to: 'To {{name}}',
        originalAmount: 'Original amount',
        balanceLabel: 'Open balance',
        dateLabel: 'Opened',
        dueDateLabel: 'Due date',
        reasonLabel: 'Reason',
        trustLabel: 'Trust basis',
        contextLabel: 'Context',
        descriptionLabel: 'Description',
        accountLabel: 'Account',
        statusClosed: 'Settled',
        statusArchived: 'Archived',
        statusOpen: 'Open',
        types: {
          incoming: 'Owed to me',
          outgoing: 'I owe',
        },
        originLabels: {
          borrowed_cash: 'Borrowed cash',
          lent_cash: 'Lent cash',
          incurred_liability: 'Obligation without cash',
          imported_existing: 'Imported existing balance',
          manual_correction: 'Manual correction',
          legacy_cash_assumed: 'Cash-linked debt',
        },
      },
      form: {
        editTitle: 'Edit debt',
        addTitle: 'Add debt',
        save: 'Save',
        originType: 'What happened?',
        originHint: 'Choose whether this debt created real account cash movement or only an obligation to track.',
        originOptions: {
          cashLinked: 'Cash moved through an account',
          incurredLiability: 'I owe money without receiving cash',
          importedExisting: 'Existing balance I want to track',
        },
        direction: 'Direction',
        directionOptions: {
          outgoing: 'I owe',
          incoming: 'Owed to me',
        },
        fromWhom: 'From whom',
        toWhom: 'To whom',
        amount: 'Amount',
        amountHint: 'Decimals are supported. Use a dot, for example: 12.34',
        date: 'Debt date',
        dueDate: 'Due date',
        currency: 'Currency',
        currencyHint: 'Used for obligations that are not linked to an account transaction.',
        account: 'Account',
        accountPlaceholder: 'Select account',
        reason: 'Reason',
        trustBasis: 'Trust basis',
        emotionalContext: 'Context',
        description: 'Description',
      },
      transactions: {
        types: {
          repaid: 'Repaid',
          borrowed: 'Borrowed',
          borrowedMore: 'Borrowed more',
          lentMore: 'Lent more',
        },
        modalTitle: '{{type}} transaction',
        save: 'Save',
        amount: 'Amount',
        amountHint: 'Decimals are supported. Use a dot, for example: 12.34',
        date: 'Date',
        account: 'Account',
        accountPlaceholder: 'Select account',
        note: 'Note',
      },
      paymentTasks: {
        title: 'Payment tasks: {{party}}',
        save: 'Create tasks',
        noticeTitle: 'These are confirmation tasks',
        noticeDescription: 'They will appear in Tasks to Confirm. When you confirm one, FinApp records a debt repayment transaction and updates the debt balance rules.',
        account: 'Pay from / receive to account',
        accountPlaceholder: 'Select account',
        accountBalance: 'Balance:',
        sharedNote: 'Shared note',
        installmentTitle: 'Task {{number}}',
        amount: 'Amount',
        amountHint: 'Decimals are supported. Use a dot, for example: 12.34',
        dueDate: 'Due date',
        note: 'Task note',
        add: 'Add another task',
        remove: 'Remove task',
        created: '{{count}} task created',
        createError: '{{error}}',
      },
      resolve: {
        title: 'Resolve debt without account payment',
        save: 'Save resolution',
        archiveBlockedTitle: 'This debt still has an open balance',
        archiveBlockedDescription: 'Archive is only a lifecycle action. First record what happened financially, then the settled debt can be archived.',
        noCashTitle: 'No account transaction will be created',
        noCashDescription: 'Use this only for forgiveness, write-off, or settlement discount. If money actually moved from an account, use the repayment action instead.',
        mistakeVoidTitle: 'Mistake or test correction',
        mistakeVoidDescription: 'Use this only when the debt record itself was wrong. If linked cash exists, choose whether that cash movement should stay real or be reversed.',
        type: 'Resolution type',
        types: {
          forgiven: 'The remaining debt was forgiven',
          writeOff: 'I am no longer required to pay this',
          settlementDiscount: 'Settled for less than the full balance',
          mistakeVoid: 'This was a mistake or test',
        },
        cashDecision: 'What should happen to linked cash movement?',
        cashDecisionHint: 'This prevents hiding a fake debt while leaving an unexplained cash transaction in your ledger.',
        cashDecisionRequired: 'Choose what to do with linked cash movement',
        cashDecisions: {
          keep: 'Keep cash movement as real, void only the debt relation',
          reverse: 'Reverse linked cash movement too',
        },
        amount: 'Amount resolved',
        amountHint: 'Leave empty to resolve the full open balance.',
        effectiveDate: 'Effective date',
        note: 'Note',
      },
      history: {
        title: 'Transactions: {{party}}',
        defaultParty: 'Debt',
        loading: 'Loading...',
        empty: 'No transactions found.',
        transactionsTitle: 'Account transactions',
        itemTitle: '{{type}}',
        itemDate: 'Date: {{date}}',
        itemNote: 'Note: {{note}}',
        noNote: 'No note',
      },
      events: {
        title: 'Debt events',
        itemDate: 'Effective: {{date}}',
        itemEffect: 'Balance effect: {{effect}}',
        types: {
          opened: 'Opened',
          borrowed: 'Borrowed',
          repaid: 'Repaid',
          forgiven: 'Forgiven',
          write_off: 'Write-off',
          settlement_discount: 'Settlement discount',
          correction: 'Correction',
          manual_correction: 'Manual correction',
          voided: 'Voided',
          archived: 'Archived',
          restored: 'Restored',
        },
        effects: {
          increase_outstanding: 'increases outstanding balance',
          decrease_outstanding: 'decreases outstanding balance',
          none: 'no balance effect',
        },
      },
      messages: {
        loadTransactionsError: 'Failed to load transactions',
        loadDebtsError: 'Failed to load debts',
        loadAccountsError: 'Failed to load accounts',
        debtUpdated: 'Debt updated',
        debtCreated: 'Debt created',
        debtDeleted: 'Debt deleted',
        debtArchived: 'Debt archived',
        debtRestored: 'Debt restored',
        debtResolved: 'Debt resolution saved',
        transactionAdded: 'Transaction added',
        errorWithReason: '{{error}}',
      },
      tabs: {
        current: 'Current',
        archived: 'Archived',
      },
    },
  },
  uk: {
    debtManager: {
      title: 'Борги',
      pageDescription: 'Відстежуйте, кому винні ви, хто винен вам, і тримайте реєстр боргів чистим без змішування активних та архівних записів.',
      actions: {
        add: 'Додати борг',
        edit: 'Редагувати',
        history: 'Історія',
        archive: 'В архів',
        restore: 'Повернути',
        delete: 'Видалити',
        resolve: 'Закрити без платежу',
        paymentTasks: 'Створити задачі платежів',
        cancel: 'Скасувати',
        confirmDeleteTitle: 'Видалити цей борг?',
        confirmArchiveTitle: 'Перемістити цей борг до архіву?',
        confirmRestoreTitle: 'Повернути цей борг до поточних?',
      },
      accessibility: {
        debtActions: 'Дії з боргом',
      },
      summary: {
        tracked: {
          label: 'Боргів у реєстрі',
          supporting: 'Поточні та архівні записи',
        },
        active: {
          label: 'Активні борги',
          supporting: 'Ще мають відкритий баланс',
        },
        settled: {
          label: 'Погашені записи',
          supporting: 'Можна перемістити в архів',
        },
        outstanding: {
          label: 'Відкритий баланс',
          estimatedLabel: 'Оціночний відкритий баланс',
          supporting: '{{count}} в архіві',
          mixedValue: 'Різні валюти',
        },
      },
      valuation: {
        estimatedTitle: 'Відкритий баланс боргів уже працює на явній моделі оцінки',
        estimatedDescription: 'Цей підсумок оцінює борги у {{currency}} на основі нативних записів боргу та кешованих FX-курсів.',
        staleTitle: 'Відкритий баланс боргів використовує застарілі FX-курси',
        staleDescription: 'Цей підсумок оцінює борги у {{currency}}, але частина FX-курсів застаріла. Підсумки можуть оновитися після refresh.',
        degradedTitle: 'Оцінка відкритого балансу боргів частково деградована',
        degradedDescription: 'Цей підсумок оцінює борги у {{currency}}, але частина FX-курсів відсутня або завершилася помилкою. Підсумки можуть бути неповними до успішного refresh.',
        partialTitle: 'Відкритий баланс боргів оцінено лише частково',
        partialDescription: 'Цей підсумок оцінює борги у {{currency}}, але частину боргів неможливо оцінити з поточним набором курсів.'
      },
      sections: {
        registry: {
          title: 'Реєстр боргів',
          description: 'Тримайте активні борги в полі зору, архівуйте погашені та записуйте позики й повернення в одному місці.',
        },
      },
      notices: {
        archiveTitle: 'Архівуйте погашені борги',
        archiveDescription: '{{count}} погашений борг можна перенести в архів, щоб активний список залишався чистим.',
      },
      list: {
        empty: 'Боргів ще немає',
        emptyArchived: 'Архівних боргів ще немає',
        from: 'Від {{name}}',
        to: 'Кому {{name}}',
        originalAmount: 'Початкова сума',
        balanceLabel: 'Відкритий баланс',
        dateLabel: 'Створено',
        dueDateLabel: 'Строк',
        reasonLabel: 'Причина',
        trustLabel: 'Підстава довіри',
        contextLabel: 'Контекст',
        descriptionLabel: 'Опис',
        accountLabel: 'Рахунок',
        statusClosed: 'Погашено',
        statusArchived: 'Архів',
        statusOpen: 'Відкрито',
        types: {
          incoming: 'Мені винні',
          outgoing: 'Я винен',
        },
        originLabels: {
          borrowed_cash: 'Позичені гроші',
          lent_cash: 'Я позичив гроші',
          incurred_liability: 'Зобовʼязання без отримання грошей',
          imported_existing: 'Імпортований початковий баланс',
          manual_correction: 'Ручне коригування',
          legacy_cash_assumed: 'Борг з рухом грошей',
        },
      },
      form: {
        editTitle: 'Редагувати борг',
        addTitle: 'Додати борг',
        save: 'Зберегти',
        originType: 'Що сталося?',
        originHint: 'Оберіть, чи був реальний рух грошей по рахунку, чи це лише зобовʼязання для обліку.',
        originOptions: {
          cashLinked: 'Гроші пройшли через рахунок',
          incurredLiability: 'Я винен гроші, але не отримував cash',
          importedExisting: 'Існуючий баланс для обліку',
        },
        direction: 'Напрямок',
        directionOptions: {
          outgoing: 'Я винен',
          incoming: 'Мені винні',
        },
        fromWhom: 'Від кого',
        toWhom: 'Кому',
        amount: 'Сума',
        amountHint: 'Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34',
        date: 'Дата боргу',
        dueDate: 'Строк',
        currency: 'Валюта',
        currencyHint: 'Використовується для зобовʼязань без транзакції по рахунку.',
        account: 'Рахунок',
        accountPlaceholder: 'Оберіть рахунок',
        reason: 'Причина',
        trustBasis: 'Підстава довіри',
        emotionalContext: 'Контекст',
        description: 'Опис',
      },
      transactions: {
        types: {
          repaid: 'Повернення',
          borrowed: 'Позика',
          borrowedMore: 'Позичив ще',
          lentMore: 'Позичив комусь ще',
        },
        modalTitle: '{{type}}',
        save: 'Зберегти',
        amount: 'Сума',
        amountHint: 'Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34',
        date: 'Дата',
        account: 'Рахунок',
        accountPlaceholder: 'Оберіть рахунок',
        note: 'Нотатка',
      },
      paymentTasks: {
        title: 'Задачі платежів: {{party}}',
        save: 'Створити задачі',
        noticeTitle: 'Це задачі на підтвердження',
        noticeDescription: 'Вони зʼявляться у Tasks to Confirm. Коли ви підтвердите задачу, FinApp запише repayment-транзакцію по боргу та оновить правила балансу боргу.',
        account: 'З якого / на який рахунок',
        accountPlaceholder: 'Оберіть рахунок',
        accountBalance: 'Баланс:',
        sharedNote: 'Загальна нотатка',
        installmentTitle: 'Задача {{number}}',
        amount: 'Сума',
        amountHint: 'Дробова частина підтримується. Використовуйте крапку, наприклад: 12.34',
        dueDate: 'Дата платежу',
        note: 'Нотатка до задачі',
        add: 'Додати ще задачу',
        remove: 'Прибрати задачу',
        created: 'Створено задач: {{count}}',
        createError: '{{error}}',
      },
      resolve: {
        title: 'Закрити борг без платежу з рахунку',
        save: 'Зберегти закриття',
        archiveBlockedTitle: 'У цього боргу ще є відкритий баланс',
        archiveBlockedDescription: 'Архів — це лише життєвий стан запису. Спочатку зафіксуйте, що сталося фінансово, після цього погашений борг можна архівувати.',
        noCashTitle: 'Транзакція по рахунку не буде створена',
        noCashDescription: 'Використовуйте це лише для прощення, списання або знижки при врегулюванні. Якщо гроші реально пішли з рахунку, використовуйте дію повернення.',
        mistakeVoidTitle: 'Виправлення помилки або тесту',
        mistakeVoidDescription: 'Використовуйте це лише тоді, коли сам запис боргу був помилковим. Якщо є повʼязаний рух грошей, оберіть: залишити його реальним чи сторнувати.',
        type: 'Тип закриття',
        types: {
          forgiven: 'Залишок боргу було прощено',
          writeOff: 'Я більше не зобовʼязаний це платити',
          settlementDiscount: 'Врегульовано за меншу суму',
          mistakeVoid: 'Це була помилка або тест',
        },
        cashDecision: 'Що зробити з повʼязаним рухом грошей?',
        cashDecisionHint: 'Це не дає приховати фейковий борг, залишивши незрозумілу транзакцію в ledger.',
        cashDecisionRequired: 'Оберіть, що зробити з повʼязаним рухом грошей',
        cashDecisions: {
          keep: 'Залишити рух грошей реальним, анулювати лише звʼязок з боргом',
          reverse: 'Сторнувати також повʼязаний рух грошей',
        },
        amount: 'Сума закриття',
        amountHint: 'Залиште порожнім, щоб закрити весь відкритий баланс.',
        effectiveDate: 'Дата набуття чинності',
        note: 'Нотатка',
      },
      history: {
        title: 'Транзакції: {{party}}',
        defaultParty: 'Борг',
        loading: 'Завантаження...',
        empty: 'Транзакцій не знайдено.',
        transactionsTitle: 'Транзакції рахунку',
        itemTitle: '{{type}}',
        itemDate: 'Дата: {{date}}',
        itemNote: 'Нотатка: {{note}}',
        noNote: 'Без нотатки',
      },
      events: {
        title: 'Події боргу',
        itemDate: 'Діє з: {{date}}',
        itemEffect: 'Вплив на баланс: {{effect}}',
        types: {
          opened: 'Відкрито',
          borrowed: 'Позика',
          repaid: 'Повернення',
          forgiven: 'Прощено',
          write_off: 'Списано',
          settlement_discount: 'Знижка при врегулюванні',
          correction: 'Корекція',
          manual_correction: 'Ручна корекція',
          voided: 'Анульовано',
          archived: 'Архівовано',
          restored: 'Відновлено',
        },
        effects: {
          increase_outstanding: 'збільшує відкритий баланс',
          decrease_outstanding: 'зменшує відкритий баланс',
          none: 'без впливу на баланс',
        },
      },
      messages: {
        loadTransactionsError: 'Не вдалося завантажити транзакції',
        loadDebtsError: 'Не вдалося завантажити борги',
        loadAccountsError: 'Не вдалося завантажити рахунки',
        debtUpdated: 'Борг оновлено',
        debtCreated: 'Борг створено',
        debtDeleted: 'Борг видалено',
        debtArchived: 'Борг переміщено в архів',
        debtRestored: 'Борг повернуто',
        debtResolved: 'Закриття боргу збережено',
        transactionAdded: 'Транзакцію додано',
        errorWithReason: '{{error}}',
      },
      tabs: {
        current: 'Поточні',
        archived: 'Архів',
      },
    },
  },
}

export default resources
